package com.writerslab

/**
 * Single source of truth for every event the app sends to Firebase
 * (plus the one GTM event, [purchaseConversion]).
 *
 * Every call site that wants to log analytics goes through this
 * interface, never through [Analytics.event] directly (with the
 * deliberate exception of the global error handlers in `Main.kt`,
 * which catch un-typed exceptions and forward them to
 * [Analytics.recordError]). Centralising the catalogue here gives us:
 *
 *   - one place to grep when answering "what does the app track?";
 *   - type-checked params — Kotlin enforces the right value shape so a
 *     typo in `is_free` vs `isFree` is a compile error, not a silent
 *     dimension fork in the console;
 *   - bucketing co-located with the event that needs it, so call sites
 *     pass raw numbers and don't repeat `bucketWordCount(...)` boilerplate;
 *   - one place to rename / drop / migrate events when the analytics
 *     schema changes — the wire name lives in exactly one string literal.
 *
 * The whole surface is an **interface** rather than an object so unit
 * tests can substitute a fake without booting Firebase or even
 * touching [Analytics]. Production code receives the live instance
 * through dependency injection:
 *
 *  - [MainViewModel] takes an [AnalyticsEvents] constructor parameter
 *    (production wires it in `main()`; tests pass a fake).
 *  - Composables read it from the [LocalAnalyticsEvents]
 *    CompositionLocal, provided once at the root of the tree.
 *
 * There is no top-level mutable variable — every caller either holds
 * its dependency directly or pulls it from the composition.
 *
 * Naming convention: `snake_case` on the wire, `camelCase` on the
 * Kotlin side. Verbs: started / completed / failed / opened / closed /
 * clicked / confirmed. Domain prefixes: ai_*, promo_*, payment_*,
 * checkout_*, help_*, file_*. Param keys cap at ≤40 chars (Firebase
 * limit). Numeric values come through bucket helpers from `Analytics.kt`
 * to stay inside Firebase's dimension cardinality budget.
 */
interface AnalyticsEvents {

    // ── Identity / session ─────────────────────────────────────────

    /** Bind the live Firebase user id and refresh the segmenting
     *  properties. Emits a `login` event when the resolved id differs
     *  from the previously-bound one (first sign-in, account swap on
     *  the same browser); repeated /me hydrations on the same user
     *  are no-ops. The "previously bound" state is the implementation's
     *  responsibility, not the caller's — callers just hand off the
     *  current snapshot. */
    fun bindUser(
        userId: String,
        credits: Int,
        emailVerified: Boolean,
        freeShortRemaining: Int,
        locale: String,
    )

    /** Detach the Firebase user id (e.g. on signOut or when /me reports
     *  an anonymous session). Doesn't emit `logout` — that's a separate
     *  user-initiated signal via [logout]. */
    fun unbindUser()

    fun signInClicked()
    fun logout()
    fun eulaAccepted()

    // ── File upload ───────────────────────────────────────────────

    fun fileUploadStarted(source: UploadSource)
    fun fileUploadCompleted(words: Int, language: String, qualityScore: Int?)
    fun fileUploadFailed(reason: String?)
    fun staticAnalysisReplaced()
    fun bookSelectedFromHistory(aiStatus: String)

    // ── AI pipeline ───────────────────────────────────────────────

    fun aiAnalysisStarted(wordCount: Int, isRerun: Boolean)
    fun aiAnalysisCompleted(
        durationSeconds: Int,
        finalScore: Double?,
        isFree: Boolean,
        costCredits: Int,
    )
    fun aiAnalysisFailed(reason: String?, stage: Int)
    fun aiRerunClicked()
    fun aiTabSwitched(tabName: String)

    // ── Promo materials ───────────────────────────────────────────

    fun promoGenerateStarted(kind: PromoKind, extra: Map<String, Any?> = emptyMap())
    fun promoGenerateCompleted(kind: PromoKind, durationSeconds: Int)
    fun promoGenerateFailed(
        kind: PromoKind,
        reason: String?,
        extra: Map<String, Any?> = emptyMap(),
    )
    fun promoDownload(kind: PromoKind, format: String)

    // ── Editing the text in the reader ────────────────────────────

    fun textEditStarted()
    /** The «what editing gives» card shown to someone who may not edit yet;
     *  [source]: recheck (the locked button) or typing (in the text). */
    fun textEditLocked(source: String)
    fun textSaved(paragraphsChanged: Int, marksDropped: Int)
    /** [reason]: text_changed / scan_running / book_busy / too_short / failed. */
    fun textSaveFailed(reason: String)
    fun textDownloaded()
    fun textRechecked(cost: Int, removed: Int, added: Int)
    fun textRecheckFailed(reason: String)

    // ── Payments ──────────────────────────────────────────────────

    fun topupDialogOpened()
    fun packSelected(packId: String, region: String)
    /** International rail stubs out to a "contact support" toast because
     *  the provider account has no EUR balance. This event lets us
     *  count interest in the rail so we can re-prioritise enabling it. */
    fun checkoutIntlStubbed(packId: String)
    fun checkoutStarted(packId: String, region: String)
    /** Backend returned the payment page: shown in the payment window, or —
     *  when the browser blocked that — in this tab ([inWindow] = false). */
    fun checkoutRedirected(packId: String, inWindow: Boolean)
    /** Caller passes the gross RUB price; we forward it as Firebase's
     *  `value` / `currency` so the event registers as revenue. Pass
     *  `priceRub = null` when the price isn't known (unknown packId
     *  from server) — we then ship the event without the revenue
     *  fields instead of silently zeroing them out. */
    fun paymentConfirmed(packId: String, creditsAdded: Int, priceRub: Double?)
    /** GTM `purchase` (drives the YM/GA purchase tags). Call once per
     *  payment id the backend's purchase-event claim hands out — that
     *  claim, not the client, keeps it to one event per purchase. */
    fun purchaseConversion()
    fun paymentCanceled(packId: String, status: String)
    fun paymentError(stage: PaymentErrorStage, reason: String?)
    fun redeemAttempted(result: String, creditsAdded: Int? = null)

    // ── UX ────────────────────────────────────────────────────────

    fun helpModalOpened(block: HelpBlock)
    fun metricsBlockExpanded()
    fun emailVerificationResendClicked()
    fun emailVerificationConfirmClicked()

    // ── Errors ────────────────────────────────────────────────────

    fun errorShown(kind: ErrorKind, message: String?)

    // ── Nested kinds (kept on the interface so the import surface
    //     for callers is a single symbol — `AnalyticsEvents.UploadSource`,
    //     etc. — instead of nine free-floating enums) ─────────────

    enum class UploadSource(val id: String) { PICKER("picker"), DROP("drop") }

    enum class PromoKind(val id: String) {
        ANNOTATION("annotation"),
        COVER("cover"),
        COVER_REGEN("cover_regen"),
        TEASER_PREVIEW("teaser_preview"),
        TEASER_VIDEO("teaser_video"),
        TEASER_SCENE_REGEN("teaser_scene_regen"),
    }

    enum class PaymentErrorStage(val id: String) {
        CHECKOUT_CALL("checkout_call"),
        CHECKOUT_RESPONSE("checkout_response"),
    }

    enum class ErrorKind(val id: String) {
        AI_PIPELINE("ai_pipeline"),
        STATIC_ANALYSIS("static_analysis"),
    }

    /** Identifies a help-icon location for the `help_modal_opened`
     *  event. Add an entry before adding a new BlockHelp call site —
     *  the strongly-typed param prevents typos drifting into Firebase
     *  as silent new dimensions. */
    enum class HelpBlock(val id: String) {
        AI_INSIGHTS("ai_insights"),
        AI_SCORE("ai_score"),
        PROMO_SETTINGS("promo_settings"),
        PROMO_MATERIALS("promo_materials"),
        QUALITY("quality"),
        HIGHLIGHTS("highlights"),
        BOOK_PROFILE("book_profile"),
        METRICS("metrics"),
    }
}

/**
 * Production implementation. All wire-format strings and bucketing
 * live here. Tests prefer a lightweight fake that records calls;
 * this class never has to be exercised directly in unit tests.
 *
 * The `login`-on-id-change gate is implemented here instead of in
 * callers because it's a concern about how this implementation talks
 * to Firebase (don't re-fire the event for the same user), not a
 * domain concern.
 */
class DefaultAnalyticsEvents(
    private val sink: AnalyticsSink = FirebaseAnalyticsSink,
) : AnalyticsEvents {

    private var lastBoundUserId: String? = null

    // ── Identity / session ─────────────────────────────────────────

    override fun bindUser(
        userId: String,
        credits: Int,
        emailVerified: Boolean,
        freeShortRemaining: Int,
        locale: String,
    ) {
        if (userId != lastBoundUserId) {
            sink.setUserId(userId)
            sink.event("login")
            lastBoundUserId = userId
        }
        sink.setUserProperty("credits_bucket", bucketCredits(credits))
        sink.setUserProperty("email_verified", emailVerified.toString())
        sink.setUserProperty("language", locale)
        sink.setUserProperty(
            "free_short_remaining_bucket",
            bucketFreeShortRemaining(freeShortRemaining),
        )
    }

    override fun unbindUser() {
        sink.setUserId(null)
        lastBoundUserId = null
    }

    override fun signInClicked() = sink.event("signin_clicked")
    override fun logout() = sink.event("logout")
    override fun eulaAccepted() = sink.event("eula_accepted")

    // ── File upload ───────────────────────────────────────────────

    override fun fileUploadStarted(source: AnalyticsEvents.UploadSource) =
        sink.event("file_upload_started", mapOf("source" to source.id))

    override fun fileUploadCompleted(words: Int, language: String, qualityScore: Int?) =
        sink.event("file_upload_completed", mapOf(
            "word_count_bucket" to bucketWordCount(words),
            "language" to language,
            "score_bucket" to bucketScore(qualityScore?.toDouble()),
        ))

    override fun fileUploadFailed(reason: String?) =
        sink.event("file_upload_failed", mapOf(
            "reason" to (reason?.take(80) ?: "unknown"),
        ))

    override fun staticAnalysisReplaced() = sink.event("static_analysis_replaced")

    override fun bookSelectedFromHistory(aiStatus: String) =
        sink.event("book_selected_from_history", mapOf("ai_status" to aiStatus))

    // ── AI pipeline ───────────────────────────────────────────────

    override fun aiAnalysisStarted(wordCount: Int, isRerun: Boolean) =
        sink.event("ai_analysis_started", mapOf(
            "word_count_bucket" to bucketWordCount(wordCount),
            "is_rerun" to isRerun,
        ))

    override fun aiAnalysisCompleted(
        durationSeconds: Int,
        finalScore: Double?,
        isFree: Boolean,
        costCredits: Int,
    ) = sink.event("ai_analysis_completed", mapOf(
        "duration_bucket" to bucketDurationSeconds(durationSeconds),
        "final_score_bucket" to bucketScore(finalScore),
        "is_free" to isFree,
        "cost_credits" to costCredits,
    ))

    override fun aiAnalysisFailed(reason: String?, stage: Int) =
        sink.event("ai_analysis_failed", mapOf(
            "reason" to (reason?.take(80) ?: "unknown"),
            "stage" to stage,
        ))

    override fun aiRerunClicked() = sink.event("ai_rerun_clicked")

    override fun aiTabSwitched(tabName: String) =
        sink.event("ai_tab_switched", mapOf("tab" to tabName.lowercase()))

    // ── Promo materials ───────────────────────────────────────────

    override fun promoGenerateStarted(
        kind: AnalyticsEvents.PromoKind,
        extra: Map<String, Any?>,
    ) = sink.event("promo_generate_started", buildMap {
        put("kind", kind.id)
        putAll(extra)
    })

    override fun promoGenerateCompleted(
        kind: AnalyticsEvents.PromoKind,
        durationSeconds: Int,
    ) = sink.event("promo_generate_completed", mapOf(
        "kind" to kind.id,
        "duration_bucket" to bucketDurationSeconds(durationSeconds),
    ))

    override fun promoGenerateFailed(
        kind: AnalyticsEvents.PromoKind,
        reason: String?,
        extra: Map<String, Any?>,
    ) = sink.event("promo_generate_failed", buildMap {
        put("kind", kind.id)
        put("reason", reason?.take(80) ?: "unknown")
        putAll(extra)
    })

    override fun promoDownload(kind: AnalyticsEvents.PromoKind, format: String) =
        sink.event("promo_download", mapOf("kind" to kind.id, "format" to format))

    // ── Payments ──────────────────────────────────────────────────

    override fun topupDialogOpened() = sink.event("topup_dialog_opened")

    override fun packSelected(packId: String, region: String) =
        sink.event("pack_selected", mapOf("pack_id" to packId, "region" to region))

    override fun textEditStarted() = sink.event("text_edit_started")

    override fun textEditLocked(source: String) =
        sink.event("text_edit_locked", mapOf("source" to source))

    override fun textSaved(paragraphsChanged: Int, marksDropped: Int) =
        sink.event("text_saved", mapOf(
            "paragraphs_changed" to paragraphsChanged,
            "marks_dropped" to marksDropped,
        ))

    override fun textSaveFailed(reason: String) =
        sink.event("text_save_failed", mapOf("reason" to reason))

    override fun textDownloaded() = sink.event("text_downloaded")

    override fun textRechecked(cost: Int, removed: Int, added: Int) =
        sink.event("text_rechecked", mapOf("cost_credits" to cost, "removed" to removed, "added" to added))

    override fun textRecheckFailed(reason: String) =
        sink.event("text_recheck_failed", mapOf("reason" to reason.take(80)))

    override fun checkoutIntlStubbed(packId: String) =
        sink.event("checkout_intl_stubbed", mapOf("pack_id" to packId))

    override fun checkoutStarted(packId: String, region: String) =
        sink.event("checkout_started", mapOf("pack_id" to packId, "region" to region))

    override fun checkoutRedirected(packId: String, inWindow: Boolean) =
        sink.event("checkout_redirected", mapOf(
            "pack_id" to packId,
            "mode" to if (inWindow) "window" else "tab",
        ))

    override fun paymentConfirmed(packId: String, creditsAdded: Int, priceRub: Double?) =
        sink.event("payment_confirmed", buildMap {
            put("pack_id", packId)
            put("credits_added", creditsAdded)
            if (priceRub != null) {
                put("value", priceRub)
                put("currency", "RUB")
            }
        })

    override fun purchaseConversion() = GtmDataLayer.push("purchase")

    override fun paymentCanceled(packId: String, status: String) =
        sink.event("payment_canceled", mapOf("pack_id" to packId, "status" to status))

    override fun paymentError(stage: AnalyticsEvents.PaymentErrorStage, reason: String?) =
        sink.event("payment_error", mapOf(
            "stage" to stage.id,
            "reason" to (reason?.take(80) ?: "unknown"),
        ))

    override fun redeemAttempted(result: String, creditsAdded: Int?) =
        sink.event("redeem_attempted", buildMap {
            put("result", result)
            if (creditsAdded != null) put("credits_added", creditsAdded)
        })

    // ── UX ────────────────────────────────────────────────────────

    override fun helpModalOpened(block: AnalyticsEvents.HelpBlock) =
        sink.event("help_modal_opened", mapOf("block" to block.id))

    override fun metricsBlockExpanded() = sink.event("metrics_block_expanded")

    override fun emailVerificationResendClicked() =
        sink.event("email_verification_resend_clicked")

    override fun emailVerificationConfirmClicked() =
        sink.event("email_verification_confirm_clicked")

    // ── Errors ────────────────────────────────────────────────────

    override fun errorShown(kind: AnalyticsEvents.ErrorKind, message: String?) =
        sink.event("error_shown", mapOf(
            "kind" to kind.id,
            "message" to (message?.take(80) ?: ""),
        ))
}

/**
 * Tiny destination interface for events — the seam where
 * [DefaultAnalyticsEvents] hands its already-composed wire-format
 * payload over to whatever actually ships it. In production the sink
 * is [FirebaseAnalyticsSink]; tests supply a recording fake so they
 * can assert on the raw event name and params without involving
 * Firebase or the global [Analytics] object.
 *
 * Called "sink" because that's the established term for "where
 * observability output goes" (Serilog, structured-logging frameworks
 * use the same word). Not called "backend" — that's already the
 * Spring service in this repo and the reuse would mislead.
 */
interface AnalyticsSink {
    fun event(name: String, params: Map<String, Any?> = emptyMap())
    fun setUserId(id: String?)
    fun setUserProperty(name: String, value: String?)
}

/** Production sink. Forwards every call to the global [Analytics]
 *  object, which forwards to the Firebase JS SDK when initialised
 *  and turns into a no-op when it isn't (missing config, init failure,
 *  opt-out). */
object FirebaseAnalyticsSink : AnalyticsSink {
    override fun event(name: String, params: Map<String, Any?>) =
        Analytics.event(name, params)
    override fun setUserId(id: String?) = Analytics.setUserId(id)
    override fun setUserProperty(name: String, value: String?) =
        Analytics.setUserProperty(name, value)
}

/**
 * CompositionLocal carrying the active [AnalyticsEvents] instance to
 * every Composable that needs to log an event (help-button clicks,
 * tab switches, expand/collapse, …). Provided once at the root of
 * the tree in `Main.kt`; consumers read it via
 * `LocalAnalyticsEvents.current` instead of grabbing a global.
 *
 * Non-Composable code (the [MainViewModel] and friends) gets the same
 * instance through constructor injection — there is no longer a
 * top-level mutable variable. Both paths converge on a single
 * production instance created in `main()`; tests can inject a fake
 * via the VM constructor and via [CompositionLocalProvider] in
 * Composable tests.
 *
 * `staticCompositionLocalOf` is the right flavour here because the
 * value never changes during the lifetime of the composition — a
 * provider switch isn't expected, so we don't pay the read-tracking
 * overhead of the default `compositionLocalOf`.
 */
val LocalAnalyticsEvents = androidx.compose.runtime.staticCompositionLocalOf<AnalyticsEvents> {
    error(
        "AnalyticsEvents not provided. Wrap the Composable tree in " +
            "CompositionLocalProvider(LocalAnalyticsEvents provides …) at the root."
    )
}
